/**
 * Armed-round cryptography — spec 8.2.
 *
 * The prompt is pre-delivered as ciphertext at T-500ms so the reveal path
 * carries no network round trip, and the key lands at T+0. A patched client
 * therefore cannot buy itself half a second of thinking time.
 *
 * The answer is never in the payload (invariant 5), so this is a timing
 * control, not a confidentiality control.
 */

import { base64ToBytes, bytesToBase64 } from './base64';

const ALGO = 'AES-GCM';
const KEY_BITS = 128;
const IV_BYTES = 12;

function subtle(): SubtleCrypto {
  const c = globalThis.crypto;
  if (!c?.subtle) {
    throw new Error('Web Crypto unavailable. Worldly needs a secure context (https or localhost)');
  }
  return c.subtle;
}

export async function importDecryptKey(keyB64: string): Promise<CryptoKey> {
  const raw = base64ToBytes(keyB64);
  return subtle().importKey('raw', raw as BufferSource, ALGO, false, ['decrypt']);
}

/** The same key, imported for sealing. Local driver only — see `sealJson`. */
export async function importSealKey(keyB64: string): Promise<CryptoKey> {
  const raw = base64ToBytes(keyB64);
  return subtle().importKey('raw', raw as BufferSource, ALGO, false, ['encrypt']);
}

/** A fresh raw AES key, base64. */
export function randomKeyB64(): string {
  return bytesToBase64(globalThis.crypto.getRandomValues(new Uint8Array(KEY_BITS / 8)));
}

/** Decrypt a base64 AES-GCM payload and parse it as JSON. */
export async function decryptJson(
  keyB64: string,
  ivB64: string,
  ciphertextB64: string,
): Promise<unknown> {
  const key = await importDecryptKey(keyB64);
  const iv = base64ToBytes(ivB64);
  const data = base64ToBytes(ciphertextB64);
  const plain = await subtle().decrypt({ name: ALGO, iv: iv as BufferSource }, key, data as BufferSource);
  return JSON.parse(new TextDecoder().decode(plain)) as unknown;
}

export interface SealedPayload {
  readonly keyB64: string;
  readonly ivB64: string;
  readonly ciphertextB64: string;
}

/**
 * Seal a payload exactly the way the gateway does. Used by the local driver
 * (`net/local.ts`) so offline play exercises the identical arm/key/reveal path
 * rather than a shortcut that would hide reveal bugs.
 */
export async function sealJson(value: unknown, keyB64?: string): Promise<SealedPayload> {
  const s = subtle();
  // A caller may supply the key when the SAME key has to open two things — a
  // rung's prompt and that rung's artwork bundle, which is how the preload
  // stays unreadable until the rung opens. See `net/local.ts`.
  const rawB64 = keyB64 ?? randomKeyB64();
  const key = await importSealKey(rawB64);
  const iv = globalThis.crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const encoded = new TextEncoder().encode(JSON.stringify(value));
  const ciphertext = await s.encrypt({ name: ALGO, iv: iv as BufferSource }, key, encoded as BufferSource);
  return {
    keyB64: rawB64,
    ivB64: bytesToBase64(iv),
    ciphertextB64: bytesToBase64(new Uint8Array(ciphertext)),
  };
}

/**
 * Seal raw bytes into `iv || ciphertext || tag` — the exact layout the gateway
 * writes for an asset bundle, and the exact layout `openSealedBytes` reads.
 */
export async function sealBytes(plaintext: Uint8Array, keyB64: string): Promise<Uint8Array> {
  const key = await importSealKey(keyB64);
  const iv = globalThis.crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const ct = await subtle().encrypt(
    { name: ALGO, iv: iv as BufferSource },
    key,
    plaintext as BufferSource,
  );
  const out = new Uint8Array(iv.length + ct.byteLength);
  out.set(iv, 0);
  out.set(new Uint8Array(ct), iv.length);
  return out;
}

// ---------------------------------------------------------------------------
// Inline sealed rungs
// ---------------------------------------------------------------------------

/**
 * A rung 2..N blob nested inside the already-sealed round payload. The server
 * pre-delivers every deeper rung sealed with its own key and releases that key
 * only when the player commits to the rung, because a rung-2 prompt names the
 * country and would otherwise hand over the rung-1 answer.
 *
 * Format is `sealed:1:<ivB64>:<ctB64>` — see server `roundcrypto.ts`. It must
 * stay byte-compatible with `sealInline` there; `roundcrypto.test.ts` and
 * `payload.test.ts` pin both ends.
 */
export const SEALED_PREFIX = 'sealed:1:';

export function isSealedInline(v: string): boolean {
  return v.startsWith(SEALED_PREFIX);
}

/**
 * Open one inline blob. Returns null for a blob that is not sealed, a
 * malformed one, or — the case this is really for — a key that belongs to a
 * DIFFERENT rung. `round.key` carries no rung number, so the caller tries each
 * still-sealed rung in turn and lets the GCM auth tag pick the winner.
 */
export async function openInlineJson(blob: string, keyB64: string): Promise<unknown | null> {
  if (!isSealedInline(blob)) return null;
  const rest = blob.slice(SEALED_PREFIX.length);
  const sep = rest.indexOf(':');
  if (sep <= 0) return null;
  try {
    return await decryptJson(keyB64, rest.slice(0, sep), rest.slice(sep + 1));
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Sealed binary assets
// ---------------------------------------------------------------------------

/**
 * Open a sealed asset bundle: `iv(12) || ciphertext || tag(16)`, raw bytes.
 *
 * Binary rather than base64 because the bundle is padded to a fixed size and
 * base64 would cost a third of it for nothing. Same AES-GCM, and the SAME KEY
 * as the rung whose options the artwork belongs to — see server
 * `game/assets.ts` for why those are one key and not two.
 *
 * Returns null on a wrong key or a tampered tag, exactly like the inline
 * opener, so a caller never has to tell "not mine" from "broken".
 */
export async function openSealedBytes(
  sealed: Uint8Array,
  keyB64: string,
): Promise<Uint8Array | null> {
  if (sealed.length < IV_BYTES + 16) return null;
  try {
    const key = await importDecryptKey(keyB64);
    const iv = sealed.subarray(0, IV_BYTES);
    const body = sealed.subarray(IV_BYTES);
    const plain = await subtle().decrypt(
      { name: ALGO, iv: iv as BufferSource },
      key,
      body as BufferSource,
    );
    return new Uint8Array(plain);
  } catch {
    return null;
  }
}
